import React, { useState, useEffect } from 'react';
import { useQuery, gql } from '@apollo/client';

const GET_PAYMENTS = gql`
  query GetAllPayments {
    getAllPayments {
      paymentid
      paymenttype
      merchantreferenceid
      transactiondate
      bankreferenceno
      amount
      customer_generated_id
    }
  }
`;

const PaymentList = () => {
    const [filteredPayments, setFilteredPayments] = useState([]);
    const [searchTerms, setSearchTerms] = useState({
        paymentid: '',
        customer_generated_id: '',
        paymenttype: '',
        merchantreferenceid: '',
        transactiondate: '',
        bankreferenceno: '',
        amount: ''
    });
    const [currentPage, setCurrentPage] = useState(1);
    const [paymentsPerPage] = useState(10);

    const { loading, error, data } = useQuery(GET_PAYMENTS);

    useEffect(() => {
        if (data && data.getAllPayments) {
            setFilteredPayments(data.getAllPayments);
        }
    }, [data]);

    useEffect(() => {
        if (data && data.getAllPayments) {
            const filteredResults = data.getAllPayments.filter(payment =>
                Object.keys(searchTerms).every(key =>
                    String(payment[key] || '').toLowerCase().includes(String(searchTerms[key]).toLowerCase())
                )
            );
            setFilteredPayments(filteredResults);
        }
    }, [searchTerms, data]);

    const handleSearch = (column, value) => {
        setSearchTerms(prev => ({ ...prev, [column]: value }));
    };

    const indexOfLastPayment = currentPage * paymentsPerPage;
    const indexOfFirstPayment = indexOfLastPayment - paymentsPerPage;
    const currentPayments = filteredPayments.slice(indexOfFirstPayment, indexOfLastPayment);

    const paginate = (pageNumber) => setCurrentPage(pageNumber);

    if (loading) return <div className="text-center py-4">Loading...</div>;
    if (error) return <div className="text-center py-4 text-red-600">Error: {error.message}</div>;

    const headers = {
        paymentid: 'Payment ID',
        customer_id: 'Customer ID',
        paymenttype: 'Type',
        merchantreferenceid: 'MerchantRefID',
        transactiondate: 'TransactionDate',
        bankreferenceno: 'BankRefNo',
        amount: 'Amount'
    };

    return (
        <div className="container mx-auto px-4 py-8">
            <h1 className="text-2xl font-bold mb-4">Payment List</h1>
            <div className="overflow-x-auto shadow-md sm:rounded-lg">
                <table className="w-full text-sm text-left text-gray-900">
                    <thead className="text-xs text-gray-700 uppercase bg-gray-50">
                        <tr>
                            {Object.entries(headers).map(([key, label]) => (
                                <th key={key} className="px-6 py-3">
                                    {label}
                                </th>
                            ))}
                        </tr>
                        <tr>
                            {Object.keys(searchTerms).map((key) => (
                                <th key={key} className="px-6 py-3">
                                    <input
                                        type="text"
                                        className="w-full px-2 py-1 text-sm border rounded"
                                        placeholder={`Search ${headers[key]}`}
                                        onChange={(e) => handleSearch(key, e.target.value)}
                                    />
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {currentPayments.length > 0 ? (
                            currentPayments.map((payment, index) => (
                                <tr key={index} className="bg-white border-b hover:bg-gray-50">
                                    {Object.keys(searchTerms).map((key) => (
                                        <td key={key} className="px-6 py-4 whitespace-nowrap">
                                            {payment[key]}
                                        </td>
                                    ))}
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={Object.keys(searchTerms).length} className="px-6 py-4 text-center">
                                    No payments found
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
                <div className="mt-4 flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6">
                    <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                        <div>
                            <p className="text-sm text-gray-700">
                                Showing <span className="font-medium">{indexOfFirstPayment + 1}</span> to <span className="font-medium">{Math.min(indexOfLastPayment, filteredPayments.length)}</span> of{' '}
                                <span className="font-medium">{filteredPayments.length}</span> results
                            </p>
                        </div>
                        <div>
                            <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                                {[...Array(Math.ceil(filteredPayments.length / paymentsPerPage))].map((_, index) => (
                                    <button
                                        key={index}
                                        onClick={() => paginate(index + 1)}
                                        className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${
                                            currentPage === index + 1
                                                ? 'z-10 bg-indigo-600 text-white'
                                                : 'text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50'
                                        }`}
                                    >
                                        {index + 1}
                                    </button>
                                ))}
                            </nav>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PaymentList;